import { BaseInfoCard, baseCardStyles } from "./base-info-card.js";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    ${baseCardStyles}

    .icon-container {
      width: 3rem;
      height: 3rem;
      background-color: var(--accent-bg, color-mix(in srgb, var(--accent-color, var(--p-blue-600, #2563eb)) 12%, transparent));
      color: var(--accent-color, var(--p-blue-600, #2563eb));
    }

    .icon-container svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    .card-header-row {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.25rem 0.375rem;
      margin-bottom: 0.375rem;
    }

    .card-title {
      line-height: 1.25;
      flex: 1;
      min-width: 65px;
    }

    .week-badge {
      font-size: 0.6875rem;
      font-weight: 600;
      padding: 0.125rem 0.45rem;
      border-radius: var(--p-badge-border-radius, 9999px);
      background-color: var(--accent-bg, color-mix(in srgb, var(--accent-color, var(--p-blue-600, #2563eb)) 12%, transparent));
      color: var(--accent-color, var(--p-blue-600, #2563eb));
      white-space: nowrap;
      flex-shrink: 0;
    }

    .card-value {
      display: flex;
      align-items: baseline;
      gap: 0.35rem;
    }

    .submitted-num {
      color: var(--p-text-color, #334155);
    }

    :host-context(.app-dark) .submitted-num,
    :host-context(.dark) .submitted-num,
    :host-context(.p-dark) .submitted-num,
    :host-context([data-theme="dark"]) .submitted-num,
    :host-context([theme="dark"]) .submitted-num,
    :host([theme="dark"]) .submitted-num {
      color: var(--p-text-color, #ffffff);
    }

    .total-num {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--p-text-muted-color, #64748b);
    }

    :host-context(.app-dark) .total-num,
    :host-context(.dark) .total-num,
    :host-context(.p-dark) .total-num,
    :host-context([data-theme="dark"]) .total-num,
    :host-context([theme="dark"]) .total-num,
    :host([theme="dark"]) .total-num {
      color: var(--p-text-muted-color, #a1a1aa);
    }

    .progress-bar {
      background-color: var(--accent-color, var(--p-primary-color, #2563eb));
    }
  </style>

  <div class="info-card" id="card" data-route="/service/lite_schedule/availability">
    <div class="card-top">
      <div class="icon-container">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
          <path d="M8 14h.01"/>
          <path d="M12 14h.01"/>
          <path d="M16 14h.01"/>
          <path d="M8 18h.01"/>
          <path d="M12 18h.01"/>
        </svg>
      </div>
      <div class="card-body">
        <div class="card-header-row">
          <h3 class="card-title" id="title">זמינות שהוגשה</h3>
          <span class="week-badge" id="weekBadge">שבוע נוכחי</span>
        </div>
        <div class="card-value">
          <span class="submitted-num" id="submittedNum">0</span>
          <span class="total-num" id="totalNum">/ 0</span>
        </div>
        <p class="card-subtitle" id="subtitle">0% הוגשו</p>
      </div>
    </div>
    <div class="progress-container">
      <div class="progress-bar" id="progressBar" style="width: 0%"></div>
    </div>
  </div>
`;

class AvailabilityCard extends BaseInfoCard {
  constructor() {
    super(template);
  }

  static get observedAttributes() {
    return ["theme", "lang", "week", "color", "secondary-color"];
  }

  get defaultRoute() {
    return "/service/lite_schedule/availability";
  }

  _getWeek() {
    const w = (this.getAttribute("week") || "current").trim().toLowerCase();
    return w === "next" ? "next" : "current";
  }

  _applyColorTheme() {
    const colorAttr = (this.getAttribute("color") || "").trim();
    const secColorAttr = (this.getAttribute("secondary-color") || "").trim();
    const isDark = this.hasAttribute("theme") && this.getAttribute("theme") === "dark";

    const presets = {
      blue: {
        light: "var(--p-blue-600, #2563eb)",
        lightBg: "color-mix(in srgb, var(--p-blue-500, #3b82f6) 12%, transparent)",
        dark: "var(--p-blue-400, #60a5fa)",
        darkBg: "color-mix(in srgb, var(--p-blue-400, #60a5fa) 20%, transparent)"
      },
      indigo: {
        light: "var(--p-indigo-600, #4f46e5)",
        lightBg: "color-mix(in srgb, var(--p-indigo-500, #6366f1) 12%, transparent)",
        dark: "var(--p-indigo-400, #818cf8)",
        darkBg: "color-mix(in srgb, var(--p-indigo-400, #818cf8) 20%, transparent)"
      },
      cyan: {
        light: "var(--p-cyan-600, #0891b2)",
        lightBg: "color-mix(in srgb, var(--p-cyan-500, #06b6d4) 12%, transparent)",
        dark: "var(--p-cyan-400, #22d3ee)",
        darkBg: "color-mix(in srgb, var(--p-cyan-400, #22d3ee) 20%, transparent)"
      },
      purple: {
        light: "var(--p-purple-600, #9333ea)",
        lightBg: "color-mix(in srgb, var(--p-purple-500, #a855f7) 12%, transparent)",
        dark: "var(--p-purple-400, #c084fc)",
        darkBg: "color-mix(in srgb, var(--p-purple-400, #c084fc) 20%, transparent)"
      },
      orange: {
        light: "var(--p-orange-600, #ea580c)",
        lightBg: "color-mix(in srgb, var(--p-orange-500, #f97316) 12%, transparent)",
        dark: "var(--p-orange-400, #fb923c)",
        darkBg: "color-mix(in srgb, var(--p-orange-400, #fb923c) 20%, transparent)"
      },
      teal: {
        light: "var(--p-teal-600, #0d9488)",
        lightBg: "color-mix(in srgb, var(--p-teal-500, #14b8a6) 12%, transparent)",
        dark: "var(--p-teal-400, #2dd4bf)",
        darkBg: "color-mix(in srgb, var(--p-teal-400, #2dd4bf) 20%, transparent)"
      },
      emerald: {
        light: "var(--p-emerald-600, #059669)",
        lightBg: "color-mix(in srgb, var(--p-primary-color, var(--p-emerald-500, #10b981)) 12%, transparent)",
        dark: "var(--p-emerald-400, #34d399)",
        darkBg: "color-mix(in srgb, var(--p-primary-color, var(--p-emerald-400, #34d399)) 20%, transparent)"
      }
    };

    let accentColor = "";
    let accentBg = "";

    if (presets[colorAttr.toLowerCase()]) {
      const p = presets[colorAttr.toLowerCase()];
      accentColor = isDark ? p.dark : p.light;
      accentBg = secColorAttr || (isDark ? p.darkBg : p.lightBg);
    } else if (colorAttr) {
      accentColor = colorAttr;
      accentBg = secColorAttr || (isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.08)");
    } else {
      const isNext = this._getWeek() === "next";
      const p = isNext ? presets.indigo : presets.blue;
      accentColor = isDark ? p.dark : p.light;
      accentBg = secColorAttr || (isDark ? p.darkBg : p.lightBg);
    }

    const cardContainer = this.shadowRoot.getElementById("card");
    if (cardContainer) {
      cardContainer.style.setProperty("--accent-color", accentColor);
      cardContainer.style.setProperty("--accent-bg", accentBg);
    }
  }

  render() {
    const titleEl = this.shadowRoot.getElementById("title");
    const weekBadgeEl = this.shadowRoot.getElementById("weekBadge");
    const submittedNumEl = this.shadowRoot.getElementById("submittedNum");
    const totalNumEl = this.shadowRoot.getElementById("totalNum");
    const subtitleEl = this.shadowRoot.getElementById("subtitle");
    const progressBarEl = this.shadowRoot.getElementById("progressBar");

    if (!titleEl || !weekBadgeEl || !submittedNumEl || !totalNumEl || !subtitleEl || !progressBarEl) return;

    this._applyColorTheme();

    const isHebrew = this.isHebrew;
    const weekType = this._getWeek();
    const weekProp = weekType === "next" ? "nextWeek" : "currentWeek";

    const services = this._data?.services || [];
    const shiftsService = services.find(s => s.serviceKey === "shifts" || s.serviceKey === "lite_schedule");
    const shiftsState = shiftsService?.state || {};
    const employeesList = shiftsState.employees || [];
    const totalStaff = employeesList.length;

    let submittedCount = 0;
    employeesList.forEach(emp => {
      if (emp[weekProp]?.availabilitySubmitted) submittedCount++;
    });

    const availPct = totalStaff > 0 ? Math.round((submittedCount / totalStaff) * 100) : 0;

    titleEl.textContent = isHebrew ? "זמינות שהוגשה" : "Availability Submitted";
    weekBadgeEl.textContent = isHebrew
      ? (weekType === "next" ? "שבוע הבא" : "שבוע נוכחי")
      : (weekType === "next" ? "Next Week" : "Current Week");

    submittedNumEl.textContent = `${submittedCount}`;
    totalNumEl.textContent = `/ ${totalStaff}`;
    subtitleEl.textContent = isHebrew ? `${availPct}% הוגשו` : `${availPct}% submitted`;
    progressBarEl.style.width = `${Math.min(100, Math.max(0, availPct))}%`;
  }
}

if (!customElements.get("availability-card")) {
  customElements.define("availability-card", AvailabilityCard);
}

export { AvailabilityCard };
